import { useCallback, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import type { AxiosError } from "axios";
import { Form, Formik } from "formik";
import Markdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import type { UploadedFile } from "@thalamos/frontend";
import { Base64FileUpload } from "@thalamos/frontend";
import {
  validateThumbnail,
  type UpsertSupersetDashboardRequest,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  LoadingSpinner,
  renderErrorToast,
} from "~/components/design-system/index.js";

// `containsPid` starts unset for a new dashboard so the admin must actively
// choose (no default); the schema requires it before submit, and it is narrowed
// to a boolean `UpsertSupersetDashboardRequest` on save.
type SupersetDashboardFormValues = Omit<
  UpsertSupersetDashboardRequest,
  "containsPid"
> & { containsPid?: boolean };

const validationSchema = yup.object({
  displayName: yup.string().required("Display name is required"),
  dashboardId: yup
    .string()
    .required("Dashboard ID is required")
    .matches(/^\d+$/, "Dashboard ID must be a numeric string"),
  embeddedUuid: yup
    .string()
    .required("Embedded UUID is required")
    .matches(
      /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i,
      "Must be a valid UUID",
    ),
  containsPid: yup
    .boolean()
    .required(
      "Select whether this dashboard contains personal-identifiable data",
    ),
});

function DeleteButton({
  dashboardId,
  onDeleted,
}: {
  dashboardId: string;
  onDeleted: () => void;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}
      <Button
        variant="outlined"
        color="error"
        onClick={() => {
          setConfirmFn({
            message:
              "Are you sure you want to delete this dashboard? Deletion will fail if any organisation still references it.",
            confirmFn: () => {
              void (async () => {
                try {
                  const result =
                    await api.supersetDashboards.delete(dashboardId);
                  if (result.status >= 400) {
                    const message =
                      (result.data as { reason?: string })?.reason ??
                      "Failed to delete dashboard";
                    renderErrorToast({ message });
                  } else {
                    onDeleted();
                  }
                } catch (e: unknown) {
                  const axiosError = e as AxiosError<{ reason?: string }>;
                  const message =
                    axiosError.response?.data?.reason ??
                    axiosError.message ??
                    "Failed to delete";
                  renderErrorToast({ message });
                }
                setConfirmFn(null);
              })();
            },
          });
        }}
      >
        Delete
      </Button>
    </>
  );
}

export interface SupersetDashboardCreateEditInnerProps {
  isNew: boolean;
  dashboardId?: string;
  initialValues: SupersetDashboardFormValues;
  onSubmit: (values: UpsertSupersetDashboardRequest) => Promise<void>;
  onCancel: () => void;
  onDeleted: () => void;
}

export function SupersetDashboardCreateEditInner({
  isNew,
  dashboardId,
  initialValues,
  onSubmit,
  onCancel,
  onDeleted,
}: SupersetDashboardCreateEditInnerProps) {
  return (
    <Card>
      <CardHeader
        title={isNew ? "Create Superset Dashboard" : "Edit Superset Dashboard"}
      />
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values, { setFieldError }) => {
            if (values.containsPid === undefined) {
              setFieldError(
                "containsPid",
                "Select whether this dashboard contains personal-identifiable data",
              );
              return;
            }
            return onSubmit({ ...values, containsPid: values.containsPid });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  name="displayName"
                  label="Display Name"
                  value={values.displayName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.displayName && Boolean(errors.displayName)}
                  helperText={touched.displayName && errors.displayName}
                  fullWidth
                />
                <TextField
                  name="dashboardId"
                  label="Superset Dashboard ID (numeric)"
                  value={values.dashboardId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.dashboardId && Boolean(errors.dashboardId)}
                  helperText={touched.dashboardId && errors.dashboardId}
                  fullWidth
                />
                <TextField
                  name="embeddedUuid"
                  label="Embedded UUID"
                  value={values.embeddedUuid}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.embeddedUuid && Boolean(errors.embeddedUuid)}
                  helperText={touched.embeddedUuid && errors.embeddedUuid}
                  fullWidth
                />

                <FormControl
                  component="fieldset"
                  error={touched.containsPid && Boolean(errors.containsPid)}
                >
                  <FormLabel component="legend">
                    Does this dashboard contain personal-identifiable data
                    (PID)?
                  </FormLabel>
                  <RadioGroup
                    name="containsPid"
                    value={
                      values.containsPid === undefined
                        ? ""
                        : values.containsPid
                          ? "yes"
                          : "no"
                    }
                    onBlur={handleBlur}
                    onChange={(e) => {
                      void setFieldValue(
                        "containsPid",
                        e.target.value === "yes",
                      );
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes — only users with PID access may view it"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No — visible to all dashboard viewers"
                    />
                  </RadioGroup>
                  {touched.containsPid && errors.containsPid && (
                    <Typography variant="caption" color="error">
                      {errors.containsPid}
                    </Typography>
                  )}
                </FormControl>

                <Typography variant="h6" sx={{ mt: 2 }}>
                  Data Glossary (Markdown)
                </Typography>
                <Box display="flex" gap={2} sx={{ width: "100%" }}>
                  <Box sx={{ width: "50%" }}>
                    <TextField
                      name="dataGlossaryMarkdown"
                      label="Data Glossary Markdown"
                      value={values.dataGlossaryMarkdown ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={20}
                      fullWidth
                    />
                  </Box>
                  <Box
                    sx={{
                      width: "50%",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      overflow: "auto",
                      maxHeight: 500,
                      "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        border: "1px solid rgba(224, 224, 224, 1)",
                      },
                      "& th, & td": {
                        border: "1px solid rgba(224, 224, 224, 1)",
                        px: 2,
                        py: 1,
                        verticalAlign: "top",
                      },
                      "& th": {
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {values.dataGlossaryMarkdown ? (
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {values.dataGlossaryMarkdown}
                      </Markdown>
                    ) : (
                      <Typography color="text.secondary">
                        Markdown preview will appear here
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Typography variant="h6" sx={{ mt: 2 }}>
                  Thumbnail
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Optional image used as the dashboard tile. PNG or JPEG. Do not
                  upload images containing personal identifiable data (PID).
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Base64FileUpload
                    label={
                      values.thumbnail ? "Change thumbnail" : "Upload thumbnail"
                    }
                    accept="image/png,image/jpeg"
                    multi={false}
                    transcodeImage={{
                      maxWidth: 1280,
                      maxHeight: 720,
                      mimeType: "image/jpeg",
                    }}
                    onDone={(files: UploadedFile[]) => {
                      const file = files[0];
                      if (!file) return;
                      if (!file.type.startsWith("image/")) {
                        renderErrorToast({
                          message: "Thumbnail must be a PNG or JPEG image",
                        });
                        return;
                      }
                      const base64 =
                        file.transcodedImage?.base64 ??
                        (typeof file.base64 === "string" ? file.base64 : null);
                      if (base64 === null) {
                        renderErrorToast({
                          message: "Failed to read the selected image",
                        });
                        return;
                      }
                      // Enforce the same format/size limits the server applies,
                      // so oversized or non-image thumbnails are rejected up front.
                      const thumbnailError = validateThumbnail(base64);
                      if (thumbnailError) {
                        renderErrorToast({ message: thumbnailError });
                        return;
                      }
                      void setFieldValue("thumbnail", base64);
                    }}
                  />
                  {values.thumbnail && (
                    <>
                      <Box
                        component="img"
                        src={values.thumbnail}
                        alt="Thumbnail preview"
                        sx={{
                          height: 48,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2">
                          Thumbnail uploaded
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          void setFieldValue("thumbnail", null);
                        }}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </Box>

                <Box display="flex" gap={2} justifyContent="flex-end">
                  {!isNew && dashboardId && (
                    <DeleteButton
                      dashboardId={dashboardId}
                      onDeleted={onDeleted}
                    />
                  )}
                  <Button variant="outlined" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    {isNew ? "Create" : "Save"}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}

export function SupersetDashboardCreateEdit() {
  const { dashboardId } = useParams();
  const navigate = useNavigate();

  const [{ data: existingDashboard, loading, response }] = dashboardId
    ? apiHooks.supersetDashboards.get(dashboardId)
    : [{ data: null, loading: false, response: null }];

  const isNew = !loading && response?.status !== 200;

  const initialValues: SupersetDashboardFormValues = {
    displayName: existingDashboard?.displayName ?? "",
    dashboardId: existingDashboard?.dashboardId ?? "",
    embeddedUuid: existingDashboard?.embeddedUuid ?? "",
    // Undefined for a new dashboard so the admin must actively choose (no default).
    containsPid: existingDashboard?.containsPid,
    dataGlossaryMarkdown: existingDashboard?.dataGlossaryMarkdown ?? "",
    thumbnail: existingDashboard?.thumbnail ?? null,
  };

  const handleNavigateToList = useCallback(() => {
    navigate("/admin/superset-dashboards");
  }, [navigate]);

  const handleSubmit = useCallback(
    async (values: UpsertSupersetDashboardRequest) => {
      try {
        const id = dashboardId ?? uuidv4();
        if (isNew) {
          const result = await api.supersetDashboards.create(id, values);
          if (result.status !== 200) {
            renderErrorToast({
              message:
                (result.data as { reason?: string })?.reason ??
                "Failed to create dashboard",
            });
            return;
          }
        } else {
          const result = await api.supersetDashboards.update(id, values);
          if (result.status !== 200) {
            renderErrorToast({
              message:
                (result.data as { reason?: string })?.reason ??
                "Failed to update dashboard",
            });
            return;
          }
        }
        handleNavigateToList();
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : "Failed to save dashboard";
        renderErrorToast({ message });
      }
    },
    [isNew, dashboardId, handleNavigateToList],
  );

  if (!isNew && loading) {
    return <LoadingSpinner />;
  }

  return (
    <SupersetDashboardCreateEditInner
      isNew={isNew}
      dashboardId={dashboardId}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleNavigateToList}
      onDeleted={handleNavigateToList}
    />
  );
}
